/**
 * Cached client-side view of which hats the player has unlocked.
 *
 * Pre-fetched on wallet login so the chest reward roll can do
 * duplicate-avoidance synchronously. Falls back to defaults
 * (none + cap) for demo / not-logged-in.
 */

import { getWalletSessionToken } from './wallet-auth.js';

const DEFAULT_HATS = ['none', 'cap'] as const;

let cached: Set<string> = new Set(DEFAULT_HATS);

// The API lives on the game server (Railway), NOT on the page origin
// (Vercel). Use VITE_SERVER_URL like every other client fetch — pointing
// at window.location.origin hit Vercel, which has no /api rewrite and
// served index.html, so the hats fetch silently fell back to defaults and
// the wardrobe showed every earned hat as locked.
const env = (import.meta as any).env;
const SERVER_HTTP_URL = (env?.VITE_SERVER_URL || 'ws://localhost:2567').replace(/^ws/, 'http');

/** Replace the cache from the server. Safe to call multiple times. */
export async function refreshHatInventory(): Promise<Set<string>> {
  const wallet = (window as any).__currentUser?.walletAddress;
  if (!wallet) {
    cached = new Set(DEFAULT_HATS);
    return cached;
  }
  try {
    const sessionToken = getWalletSessionToken(wallet);
    if (!sessionToken) return cached;
    const res = await fetch(`${SERVER_HTTP_URL}/api/players/me/hats?wallet=${encodeURIComponent(wallet)}`, {
      headers: { Authorization: `Bearer ${sessionToken}` },
    });
    if (!res.ok) return cached;
    const data = await res.json();
    const next = new Set<string>(DEFAULT_HATS);
    for (const id of data?.hats ?? []) next.add(id);
    cached = next;
  } catch {
    // network blip — keep whatever we had
  }
  return cached;
}

/** Synchronous accessor. Returns the last known set (defaults if never fetched). */
export function getCachedHats(): Set<string> {
  return cached;
}

/** Mark a hat locally as owned (after a chest unlock fires). Avoids a
 *  round-trip while the POST is still in flight. */
export function markHatOwned(hatId: string): void {
  cached.add(hatId);
}

/** Reset to defaults — call on logout. */
export function clearHatInventory(): void {
  cached = new Set(DEFAULT_HATS);
}
